"use client";

import {
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { MessageCircle } from "react-feather";

import CTAButton from "@/components/atoms/buttons/CTAButton";
import SolutionCard, { ISolution } from "@/components/cards/SolutionCard";
import SectionHeader from "@/sections/common/SectionHeader";

// ----------------------------------------------------------------------------
// "Building blocks of a solid cybersecurity strategy" — v6 (bento + stagger).
//
// On scroll into view, cards fade + rise in sequence (80ms stagger). Adds
// the premium feel without slowing perceived load — the section is fully
// rendered server-side; the animation runs client-side once the user has
// scrolled to it.
//
// Implementation: a wrapping <div> per card carries the opacity +
// translate transition. The inner SolutionCard keeps its own
// hover:-translate-y-1 unaffected (two transforms on two elements
// instead of fighting on one).
// ----------------------------------------------------------------------------

export type ISolutionSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  solutions?: ISolution[];
};

const BENTO_LAYOUT: readonly string[] = [
  "md:col-span-4 lg:col-span-4", // 0 — HERO (wide)
  "md:col-span-2 lg:col-span-2", // 1 — narrow next to hero
  "md:col-span-2 lg:col-span-2", // 2 — row 2 left
  "md:col-span-2 lg:col-span-2", // 3 — row 2 mid
  "md:col-span-4 lg:col-span-2", // 4 — row 2 right (full on tablet)
  "md:col-span-4 lg:col-span-6", // 5 — full-width bottom banner
];

const BENTO_ACCENT: readonly (
  "hero" | "primary" | "secondary" | "yellow" | "white"
)[] = ["hero", "secondary", "yellow", "primary", "secondary", "yellow"];

const SolutionSection: FC<ISolutionSectionProps> = (props) => {
  const { heading, description, solutions, className, ...other } = props;
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    // If the user prefers reduced motion, skip the animation and just
    // show the cards immediately.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // animate once only
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={clsx("bg-white py-14 md:py-20 xl:py-24", className)}
      {...other}
    >
      <div className="container">
        <SectionHeader
          eyebrow="Our framework"
          title={heading}
          subtitle={description}
          tone="light"
          align="center"
          className="mb-12 md:mb-16 xl:mb-20"
        />

        {solutions && solutions.length > 0 && (
          <div className="grid grid-cols-1 gap-5 md:auto-rows-fr md:grid-cols-4 md:gap-6 lg:grid-cols-6 lg:gap-7">
            {solutions.map((solution, idx) => {
              const layout = BENTO_LAYOUT[idx] ?? "lg:col-span-2";
              const accent = BENTO_ACCENT[idx] ?? "white";
              // Wrapper handles entrance animation only (opacity + rise),
              // so the inner card's hover -translate-y-1 doesn't conflict.
              return (
                <div
                  key={idx}
                  className={clsx(
                    "transition-all duration-700 ease-out",
                    layout,
                    inView
                      ? "translate-y-0 opacity-100"
                      : "translate-y-6 opacity-0",
                  )}
                  style={{
                    transitionDelay: inView ? `${idx * 80}ms` : "0ms",
                  }}
                >
                  <SolutionCard
                    accent={accent}
                    icon={solution.icon}
                    heading={solution.heading}
                    description={solution.description}
                    list={solution.list}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* CTA strip — converts the visitor who scrolled the bento but
            couldn't self-select. Animates in last in the stagger sequence
            (delay = card count * 80ms) so it feels like the punctuation
            mark closing the section. */}
        <div
          className={clsx(
            "mt-10 transition-all duration-700 ease-out md:mt-12 xl:mt-14",
            inView
              ? "translate-y-0 opacity-100"
              : "translate-y-6 opacity-0",
          )}
          style={{
            transitionDelay: inView
              ? `${(solutions?.length ?? 0) * 80}ms`
              : "0ms",
          }}
        >
          <div className="flex flex-col items-start gap-5 rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50 via-white to-primary-50 p-6 md:flex-row md:items-center md:justify-between md:gap-8 md:p-7 lg:p-8">
            {/* Left: icon + copy */}
            <div className="flex items-start gap-4 md:items-center">
              <span
                aria-hidden="true"
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary-900 text-white md:h-12 md:w-12"
              >
                <MessageCircle size={20} strokeWidth={2.2} />
              </span>
              <div>
                <p className="font-bricolage-grotesque text-[clamp(1rem,1vw+0.7rem,1.25rem)] font-medium leading-tight text-black-900">
                  Not sure which fits?
                </p>
                <p className="mt-1 text-[clamp(0.875rem,0.4vw+0.75rem,1rem)] text-black-700">
                  30-minute discovery call with a senior offensive
                  engineer. Free, no obligation.
                </p>
              </div>
            </div>

            {/* Right: CTA — orange (primary CTA tier). Discovery call is
                a top-of-funnel conversion event, so it sits in the same
                tier as the Hero and header "Book a Discovery Call" CTA. */}
            <div className="flex-shrink-0">
              <CTAButton
                id="solution-discovery-call"
                href="/schedule"
                variant="orange"
                radius="full"
                size="md"
                showIcon
                iconType="arrow"
                iconPosition="right"
              >
                Talk to a senior engineer
              </CTAButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
